import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import { User } from 'lucide-react';
import { useAuthStore } from '@/lib/stores/auth';
import { isStaff } from '@/lib/staffAccess';

const DASHBOARD_TAB_KEY = 'clawrent:dashboard:lastTab';

// 指示器位置跨 DashboardNav 实例持久：DashboardPage ↔ UserProfileLayout 切换时组件会重挂载，
// 用上次位置作初始值，滑动动画才能从旧 tab 滑到新 tab（而不是从 0 闪过去）。
let lastIndicator = { left: 0, width: 0 };

export function DashboardNav() {
  const { t } = useTranslation('dashboard');
  const { user } = useAuthStore();
  const location = useLocation();
  const { locale } = useParams<{ locale: string }>();
  const prefix = `/${locale}`;

  const isAdminView = location.pathname.startsWith(`${prefix}/dashboard/admin`);
  const isProviderView =
    !isAdminView &&
    (location.pathname.startsWith(`${prefix}/dashboard/provider`) ||
      location.pathname.startsWith(`${prefix}/dashboard/capabilities`) ||
      new URLSearchParams(location.search).get('view') === 'provider');
  const isHomeView =
    user?.id != null && location.pathname.startsWith(`${prefix}/users/${user.id}`);
  const isConsumerView = !isAdminView && !isProviderView && !isHomeView;

  const isAdmin = user?.role === 'admin' || isStaff(user);

  // 滑动指示器：measure 当前 active tab 的位置/宽度，切换 tab 时 transition 滑动
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState(lastIndicator);

  // useLayoutEffect 在 paint 前同步 measure，避免首帧指示器停在错误位置闪一下；
  // 初始值用 lastIndicator（跨重挂载持久），新实例从旧 tab 位置滑到当前 tab。
  useLayoutEffect(() => {
    const el = containerRef.current?.querySelector('[data-active="true"]') as HTMLElement | null;
    if (el) {
      const next = { left: el.offsetLeft, width: el.offsetWidth };
      setIndicator(next);
      lastIndicator = next;
    }
  }, [location.pathname, location.search, user?.id, isAdmin]);

  // Save last tab
  useEffect(() => {
    try {
      localStorage.setItem(DASHBOARD_TAB_KEY, location.pathname + location.search);
    } catch {}
  }, [location]);

  const tabBase =
    'relative z-10 flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap';

  return (
    <div className="mt-6">
      <div
        ref={containerRef}
        className="relative flex items-center gap-1 bg-surface-2 rounded-xl p-1 w-fit overflow-x-auto"
      >
        {/* 滑动指示器（active 背景，绝对定位，transition 滑动） */}
        <div
          className="pointer-events-none absolute top-1 bottom-1 rounded-lg bg-surface-0 shadow-soft transition-[left,width] duration-300 ease-out-expo"
          style={{ left: indicator.left, width: indicator.width }}
        />
        <Link
          to={`${prefix}/users/${user?.id}`}
          data-active={isHomeView ? 'true' : undefined}
          className={`${tabBase} ${isHomeView ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
        >
          <User className="w-4 h-4" />
          {t('tabs.home')}
        </Link>
        <Link
          to={`${prefix}/dashboard/sessions`}
          data-active={isConsumerView ? 'true' : undefined}
          className={`${tabBase} ${isConsumerView ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
        >
          {t('tabs.consumer')}
        </Link>
        <Link
          to={`${prefix}/dashboard/provider`}
          data-active={isProviderView ? 'true' : undefined}
          className={`${tabBase} ${isProviderView ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
        >
          {t('tabs.provider')}
        </Link>
        {isAdmin && (
          <Link
            to={`${prefix}/dashboard/admin`}
            data-active={isAdminView ? 'true' : undefined}
            className={`${tabBase} ${isAdminView ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {t('tabs.admin')}
          </Link>
        )}
      </div>
    </div>
  );
}
